import { useEffect, useState } from "react";
import {
  ErrorComponent,
  Outlet,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { useSwUpdate } from "@/hooks/use-sw-update";
import { SwUpdateBanner } from "@/components/SwUpdateBanner";
import { tryRecoverFromStaleDeploymentChunkFailure } from "@/lib/stale-deployment-recovery";

export interface RouterContext {
  queryClient: QueryClient;
}

function RootRouteError(props: ErrorComponentProps) {
  const recoveryResult = tryRecoverFromStaleDeploymentChunkFailure({
    source: "tanstack_router:root_error_component",
    error: props.error,
  });

  if (recoveryResult === "reloaded") {
    return null;
  }

  if (recoveryResult === "skipped") {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-[#09090b] px-4 text-zinc-100">
        <div className="w-full max-w-md rounded-xl border border-white/[0.1] bg-zinc-900/80 p-5 shadow-2xl">
          <h2 className="text-base font-semibold">Update in progress</h2>
          <p className="mt-2 text-sm text-zinc-400">
            This tab is loading an older build. Please refresh once deployment settles.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex h-9 items-center rounded-md border border-white/[0.12] bg-white/[0.04] px-3 text-sm font-medium text-zinc-100 transition-colors hover:bg-white/[0.08]"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }

  return <ErrorComponent error={props.error} />;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RootRouteError,
});

function RootLayout() {
  const { updateFound, refresh } = useSwUpdate();
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const standaloneQuery = window.matchMedia("(display-mode: standalone)");
    const updateDisplayMode = (event?: MediaQueryListEvent) => {
      const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
      const isStandalone = (event?.matches ?? standaloneQuery.matches) || iosStandalone;
      root.dataset.appDisplayMode = isStandalone ? "standalone" : "browser";
      setIsStandaloneMode(isStandalone);
    };

    updateDisplayMode();

    if (typeof standaloneQuery.addEventListener === "function") {
      standaloneQuery.addEventListener("change", updateDisplayMode);
      return () => {
        standaloneQuery.removeEventListener("change", updateDisplayMode);
        delete root.dataset.appDisplayMode;
      };
    }

    standaloneQuery.addListener(updateDisplayMode);
    return () => {
      standaloneQuery.removeListener(updateDisplayMode);
      delete root.dataset.appDisplayMode;
    };
  }, []);

  return (
    <TooltipProvider delayDuration={400}>
      <div
        className="flex flex-col"
        style={{
          /* In normal mobile browsers, use 100dvh so the container matches the
             visual viewport (accounts for dynamic Safari toolbar showing/hiding).
             In PWA standalone mode (viewport-fit=cover), the viewport extends
             behind the home indicator. 100dvh does NOT include the safe-area
             regions, which creates a gap between the app content and the screen
             edge. Using 100% inherits from #root which has -webkit-fill-available
             in standalone mode, correctly filling the entire screen. Child
             components then apply safe-area padding to avoid the home indicator. */
          height: isStandaloneMode ? "100%" : "100dvh",
        }}
      >
        {updateFound && <SwUpdateBanner onRefresh={refresh} />}
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
      <Toaster theme="dark" position="bottom-right" richColors closeButton />
    </TooltipProvider>
  );
}
